// Shared UI helpers: element builder, auth store, API, toast, modal, invite QR.
import QRCode from 'qrcode';
import { APP } from './native.js';

export const $app = document.getElementById('app');

export function h(tag, props = {}, ...children) {
  const el = document.createElement(tag);
  for (const [k, v] of Object.entries(props || {})) {
    if (v == null || v === false) continue;
    if (k.startsWith('on')) el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'class') el.className = v;
    else if (k === 'style' && typeof v === 'object') for (const [p, val] of Object.entries(v)) el.style.setProperty(p, val);
    else if (k === 'value') el.value = v;
    else el.setAttribute(k, v === true ? '' : v);
  }
  for (const c of children.flat(Infinity)) if (c != null && c !== false) el.append(c instanceof Node ? c : document.createTextNode(String(c)));
  return el;
}

/** replaceChildren that skips null/false (for conditional children) */
export const fill = (el, ...c) => el.replaceChildren(...c.flat(Infinity).filter((x) => x != null && x !== false));

/** black or white text, whichever reads better on the given hex background */
export function textOn(hex) {
  const m = String(hex).match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
  if (!m) return '#fff';
  const [r, g, b] = m.slice(1).map((x) => parseInt(x, 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.35 ? '#111' : '#fff';
}

export const auth = {
  get token() {
    return localStorage.getItem('talk.token');
  },
  get user() {
    return JSON.parse(localStorage.getItem('talk.user') || 'null');
  },
  set(token, user) {
    localStorage.setItem('talk.token', token);
    localStorage.setItem('talk.user', JSON.stringify(user));
  },
  setUser(user) {
    localStorage.setItem('talk.user', JSON.stringify(user));
  },
  clear() {
    localStorage.removeItem('talk.token');
    localStorage.removeItem('talk.user');
  },
};

let renderFn = () => {};
export const setRender = (fn) => (renderFn = fn);
export const go = (hash) => (location.hash === hash ? renderFn() : (location.hash = hash));

export async function api(method, path, body) {
  const res = await fetch(`api/${path}`, {
    method,
    headers: { 'Content-Type': 'application/json', ...(auth.token ? { Authorization: `Bearer ${auth.token}` } : {}) },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401 && path !== 'login') {
    auth.clear();
    go('#/login');
  }
  if (!res.ok) throw new Error(data.error || res.statusText);
  return data;
}

export function toast(msg, kind = 'err') {
  const el = h('div', { class: `toast ${kind}`, role: 'status' }, msg);
  document.body.append(el);
  setTimeout(() => el.remove(), 4000);
}

export const fmtDate = (ts) => (ts ? new Date(ts).toLocaleString('en-GB', { weekday: 'short', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '');
/** value for <input type=datetime-local> */
export const toLocalInput = (ts) => (ts ? new Date(ts - new Date(ts).getTimezoneOffset() * 60000).toISOString().slice(0, 16) : '');

export function modal(...content) {
  const close = () => wrap.remove();
  const wrap = h('div', { class: 'modal', onclick: (e) => e.target === wrap && close() }, h('div', { class: 'modal-box', role: 'dialog' }, h('button', { class: 'modal-x', onclick: close, 'aria-label': 'Close' }, '×'), ...content));
  document.body.append(wrap);
  return close;
}

export async function showInvite(s) {
  const svg = await QRCode.toString(s.inviteUrl, { type: 'svg', margin: 1, errorCorrectionLevel: 'M' });
  const qr = h('div', { class: 'qr' });
  qr.innerHTML = svg;
  modal(
    h('h2', {}, `Invite: ${s.name}`),
    qr,
    h('input', { class: 'mono', value: s.inviteUrl, readonly: true, onclick: (e) => e.target.select() }),
    h('div', { class: 'row' }, h('button', { onclick: () => navigator.clipboard.writeText(s.inviteUrl).then(() => toast('Link copied', 'ok'), () => toast('Copy not possible – select the link and copy it')) }, 'Copy link')),
    h('p', { class: 'muted small' }, s.guestAccess ? (s.guestPassword ? 'Guests enter their name and the guest password.' : 'Guests only enter their name.') : 'Only assigned crew members can join after signing in.'),
  );
}

export const PERM_LABEL = { admin: 'Admin', producer: 'Producer', user: 'Member' };
export const PERM_HINT = {
  admin: 'Everything: users, bases, sessions',
  producer: 'Plan, crew and start sessions',
  user: 'Joins their own sessions',
};

const MARK = '<svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 941 1032"><defs><linearGradient id="otm" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="941" y2="0"><stop offset="0" stop-color="#00a551"/><stop offset="0.25" stop-color="#04a254"/><stop offset="0.5" stop-color="#2c816f"/><stop offset="0.75" stop-color="#546089"/><stop offset="1" stop-color="#6e4b9b"/></linearGradient></defs><path fill="url(#otm)" fill-rule="evenodd" d="M191.4 1030.6C178.7 1026.5 169.4 1014.6 168.4 1001.2C167.9 995.6 169.8 983.2 179.9 926.5C186.6 889.1 192.0 856.7 192.0 854.4C192.0 844.8 186.0 833.1 178.2 827.6C170.3 822.0 168.8 821.7 137.5 820.9C113.9 820.3 106.9 819.8 100.0 818.2C49.1 806.2 10.2 763.2 2.5 710.4C1.1 700.6 1.0 671.0 1.2 420.0L1.5 140.5L3.7 132.8C12.3 102.6 29.6 81.7 56.3 69.0C66.5 64.1 73.8 62.2 86.5 61.0C100.5 59.7 542.4 59.7 551.4 61.0C563.6 62.8 572.5 69.5 577.8 80.9C580.0 85.6 580.5 87.9 580.4 95.0C580.4 102.2 580.0 104.4 577.7 109.1C574.5 115.7 566.9 123.3 560.2 126.3L555.5 128.5L340.5 129.0L125.5 129.5L117.5 132.2C97.1 139.2 83.0 152.6 76.3 171.7L73.5 179.5L73.2 433.5C73.0 605.4 73.3 690.7 74.0 697.3C76.3 719.0 88.3 735.8 108.5 745.4C123.0 752.4 121.8 752.3 191.5 752.9L254.5 753.5L259.9 756.5C270.9 762.6 277.0 773.1 277.0 786.0C277.0 789.9 275.4 802.1 273.4 813.3C269.3 836.3 269.4 840.5 274.1 849.9C279.7 861.2 290.9 868.3 302.9 868.3C310.4 868.3 316.3 866.1 323.9 860.7C389.9 813.3 469.7 757.1 473.7 755.3L478.9 752.9L518.2 753.2L557.5 753.5L562.8 756.4C569.4 760.1 574.5 765.6 577.8 772.9C580.0 777.5 580.5 780.0 580.5 787.0C580.5 794.2 580.1 796.3 577.8 801.0C574.3 808.1 568.1 814.3 561.1 817.8C555.6 820.4 555.0 820.5 532.5 821.0C514.1 821.5 508.8 821.9 505.8 823.2C503.7 824.1 438.2 870.3 360.3 925.8C282.3 981.3 216.5 1027.8 214.0 1029.0C208.5 1031.8 197.5 1032.6 191.4 1030.6ZM646.5 885.6C634.2 882.5 624.4 874.6 618.8 863.2C615.6 856.8 615.5 856.0 615.5 845.0C615.5 833.5 615.5 833.5 619.4 826.2C623.8 817.9 629.1 812.8 638.2 808.0C660.0 796.4 684.1 780.6 704.5 764.2C720.8 751.2 753.2 718.6 766.4 702.0C814.2 641.9 843.6 572.6 854.1 495.5C856.7 476.4 857.8 430.5 856.1 410.9C845.5 289.3 784.7 181.0 686.8 109.7C674.6 100.9 652.9 87.2 640.1 80.4C629.8 74.9 622.8 68.3 618.8 60.2C615.6 53.8 615.5 53.0 615.5 42.0C615.5 31.3 615.7 30.1 618.4 24.5C626.2 8.7 640.7 0.3 658.5 1.2C665.6 1.6 667.9 2.2 675.6 5.9C694.5 15.0 726.5 35.4 748.5 52.5C795.1 88.7 835.3 133.4 866.0 183.3C901.3 240.7 925.6 307.5 934.9 373.0C938.8 400.5 939.4 410.1 939.4 443.5C939.4 476.9 938.8 486.5 934.9 514.0C914.2 660.3 827.8 790.7 701.0 867.5C671.3 885.5 660.5 889.1 646.5 885.6ZM580.9 731.6C556.8 725.1 544.1 696.5 555.4 674.1C559.7 665.5 563.0 662.2 576.0 653.6C594.9 641.0 606.2 631.9 620.0 618.1C655.1 582.9 678.8 537.9 687.9 489.3C691.0 472.9 692.0 462.2 692.0 443.5C692.0 434.1 691.5 422.4 691.0 417.5C684.3 357.5 655.5 300.8 611.3 260.8C600.8 251.3 585.1 239.3 575.7 233.8C565.1 227.4 559.6 221.9 555.3 213.2C551.7 205.9 551.5 205.2 551.5 195.6C551.5 186.3 551.8 184.9 554.7 178.8C564.3 158.4 586.5 149.2 607.3 157.0C610.3 158.1 618.8 163.0 626.1 167.8C687.4 208.1 732.9 266.7 757.0 336.5C768.0 368.5 773.6 399.1 774.7 434.0C776.8 502.3 757.2 570.6 719.2 627.3C701.0 654.3 673.3 684.4 650.0 702.4C632.1 716.3 612.6 728.7 605.2 731.0C597.9 733.3 587.9 733.5 580.9 731.6ZM470.8 561.4C434.0 555.7 399.7 530.2 383.5 496.6C374.5 478.0 371.6 464.7 371.6 443.5C371.6 422.3 374.8 408.3 383.6 390.7C389.6 378.7 394.9 371.2 403.9 361.7C429.1 335.1 465.9 321.2 501.4 324.9C554.6 330.3 596.4 367.7 607.6 420.2C610.1 431.7 610.1 455.4 607.6 467.0C598.6 509.8 568.9 543.3 528.0 557.0C511.1 562.7 489.6 564.4 470.8 561.4Z"/></svg>';
export function brandMark() {
  const el = h('span', { class: 'mark' });
  el.innerHTML = MARK; // static, traced from the OUTSTANDING TALK logo
  return el;
}

/** opened as the director panel inside the Mac app (no administration links, straight into the session) */
export const STATION = (() => {
  try {
    if (new URLSearchParams(location.search).has('station')) sessionStorage.setItem('talk.station', '1');
    return sessionStorage.getItem('talk.station') === '1';
  } catch {
    return false;
  }
})();
if (STATION) document.documentElement.classList.add('in-station');

export function topbar(...right) {
  const u = auth.user;
  if (STATION || APP) right = [];
  return h(
    'header',
    { class: 'topbar' },
    h('a', { class: 'brand', href: '#/', 'aria-label': 'OUTSTANDING TALK' }, brandMark(), h('span', { class: 'wm' }, h('b', {}, 'OUTSTANDING'), ' TALK')),
    h('div', { class: 'spacer' }),
    ...right,
    u && !u.guest && !u.localMode && !STATION && !APP && h('a', { class: 'btn ghost small', href: '#/download' }, 'Mac app'),
    u && h('span', { class: 'muted small user-name' }, u.name),
    u && h('button', { class: 'ghost small', onclick: () => (auth.clear(), go('#/login')) }, 'Sign out'),
  );
}
